import {Component, DestroyRef, inject, OnInit, ViewChild} from '@angular/core';
import { SharedModule } from '../../shared/shared.module';
import { ModificationButtonsComponent } from '../../shared/components/modification-buttons/modification-buttons.component';
import { FormControl } from '@angular/forms';
import { combineLatest, map, Observable, startWith } from 'rxjs';
import { AutocompleteComponent } from '../../shared/components/autocomplete/autocomplete.component';
import { InventoryTableComponent } from './inventory-table/inventory-table.component';
import { ArrivalsTableComponent } from './arrivals-table/arrivals-table.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ContextService } from '../../shared/services/context.service';
import { TireSpec } from '../../shared/models/tireSpec';
import { NavigationConfirmService } from '../../shared/services/navigation-confirm.service';
import { TABLE_ARRIVAL_NAME, TABLE_STOCK_NAME } from '../../shared/constants/StockConstants';
import { BehaviorSubject } from 'rxjs';
import {MatDialog} from "@angular/material/dialog";
import {ImportArrivalsModalComponent} from "./import-arrivals-modal/import-arrivals-modal.component";
import {Alert, AlertService, AlertType} from "@michelin/theme";
import {TranslateService} from "@ngx-translate/core";

@Component({
  selector: 'mic-stock',
  imports: [SharedModule, ModificationButtonsComponent, AutocompleteComponent, InventoryTableComponent, ArrivalsTableComponent],
  templateUrl: './stock.component.html',
  styleUrl: './stock.component.scss'
})
export class StockComponent implements OnInit {
  siteId: number;
  TABLE_STOCK_NAME = TABLE_STOCK_NAME;
  TABLE_ARRIVAL_NAME = TABLE_ARRIVAL_NAME;

  /**
   * Properties for spinner
   */
  isLoadingData: boolean = true; //Will be set to false when data is loaded in subcomponents
  isSavingData: boolean = false;

  /**
   * Properties for managing modifications on quantity
   */
  isModificationsPending: boolean = false;

  readonly dialog = inject(MatDialog);

  /**
   * Properties for filters
   */
  // Autocomplete filter
  filterCtrl: FormControl = new FormControl<string>('');
  filteredList$: Observable<TireSpec[]>;
  tireSpecFilteredId$ = new BehaviorSubject<number | undefined>(undefined);
  // Dropdown multiple select filter
  manufacturer: FormControl<string[]> = new FormControl<string[]>([]);
  manufacturerList: string[] = [];
  manufacturerSelectedList$ = new BehaviorSubject<string[]>([]);
  manufacturerFilteredList: string[] = [];


  /**
   * Properties for table selection
   */
  selectedTable: string = TABLE_STOCK_NAME;
  @ViewChild(InventoryTableComponent) inventoryTableComponent: InventoryTableComponent;
  @ViewChild(ArrivalsTableComponent) arrivalsTableComponent: ArrivalsTableComponent;

  /**
   * Properties for date range
   */
  startYearInventory: number = new Date(new Date().setMonth(new Date().getMonth() - 11)).getFullYear();
  startMonthInventory: number = new Date(new Date().setMonth(new Date().getMonth() - 11)).getMonth() + 1;
  startYearArrivals: number = new Date().getFullYear();
  startMonthArrivals: number = new Date().getMonth() + 1;

  constructor(
    private contextService: ContextService,
    private destroyRef: DestroyRef,
    private navigationConfirm: NavigationConfirmService,
    private alertService: AlertService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.contextService.siteId$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((id) => {
      if (id !== null && id !== this.siteId) {
        this.siteId = id;
        this.resetFiltersOnSiteChange();
      }
    });

    this.manufacturer.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((selectedValues: string[]) => {
      this.manufacturerSelectedList$.next(selectedValues || []);
    });
  }

  private resetFiltersOnSiteChange(): void {
    this.tireSpecFilteredId$.next(undefined);
    this.filterCtrl.setValue('');
    this.manufacturerSelectedList$.next([]);
    this.manufacturer.setValue([]);
    this.manufacturerFilteredList = [];
    this.isLoadingData = true;
  }

  /**
   * Methods for modification buttons (save, reset)
   */
  saveModifications(): void {
    let component = this.selectedTable == TABLE_STOCK_NAME ? this.inventoryTableComponent : this.arrivalsTableComponent;
    component.saveModifications();
  }

  resetModifications(): void {
    let component = this.selectedTable == TABLE_STOCK_NAME ? this.inventoryTableComponent : this.arrivalsTableComponent;
    component.resetModifications();
  }

  /**
   * Methods for filters
   */
  private setupFilters(): void {
    let component = this.selectedTable == TABLE_STOCK_NAME ? this.inventoryTableComponent : this.arrivalsTableComponent;

    // Dropdown multiple select filter for manufacturer
    this.manufacturerList = Array.from(new Set(component.dataSource.data.map((data) => data.tireSpec.manufacturer)));

    // By default, all manufacturers are selected
    if (!this.tireSpecFilteredId$.value) {
      this.manufacturerFilteredList = this.manufacturerList;
    }

    // CombineLatest to link the autocomplete filter and the manufacturer filter
    this.filteredList$ = combineLatest([
      this.filterCtrl.valueChanges.pipe(startWith('')),
      this.manufacturer.valueChanges.pipe(startWith(this.manufacturer.value))
    ]).pipe(
      map(([spec, selectedManufacturers]) => {
        let tireSpecs = component.dataSource.data.map((data) => data.tireSpec);
        if (spec) {
          tireSpecs = tireSpecs.filter((tireSpec) => tireSpec.specification.toLowerCase().includes(spec.toLowerCase()));
        }
        if (selectedManufacturers && selectedManufacturers.length > 0) {
          tireSpecs = tireSpecs.filter((tireSpec) => selectedManufacturers.includes(tireSpec.manufacturer));
        }
        this.isLoadingData = false;
        return tireSpecs || [];
      })
    ) as Observable<TireSpec[]>;
  }

  onItemSelected(tireSpec: TireSpec): void {
    this.tireSpecFilteredId$.next(tireSpec.tireSpecId);
    // Link manufacturer filter to autocomplete filter
    this.manufacturerFilteredList = [tireSpec.manufacturer];
  }

  protected resetTireSpecFilter() {
    this.tireSpecFilteredId$.next(undefined);
    // Reset manufacturer filter when autocomplete filter is reset
    this.manufacturerFilteredList = this.manufacturerList;
  }

  onTableDataLoaded(): void {
    this.setupFilters();
  }

  openImportArrivalsModal(): void {
    const dialogRef = this.dialog.open(ImportArrivalsModalComponent, {
      data: { siteId: this.siteId },
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((result?: { success?: boolean; importedCount?: number }) => {
      this.arrivalsTableComponent?.loadStockArrivals?.();

      if (result?.success && (result.importedCount ?? 0) > 0) {
        this.alertService.showAlert({
          type: AlertType.success,
          title: this.translate.instant('stock.importArrivals.alerts.success.title'),
          content: this.translate.instant('stock.importArrivals.alerts.success.description', {
            count: result.importedCount
          }),
        } as Alert);
      }
    });
  }

  /**
   * Methods for table selection
   */
  switchTable(table: string): void {
    if (table === this.selectedTable) {
      return;
    }

    if (table === TABLE_STOCK_NAME || table === TABLE_ARRIVAL_NAME) {
      if (this.isModificationsPending) {
        this.navigationConfirm.confirmNavigation(() => {
          this.selectedTable = table;
          this.resetAllFilters();
        });
      } else {
        this.selectedTable = table;
        this.resetAllFilters();
      }
    }
  }

  private resetAllFilters(): void {
    this.tireSpecFilteredId$.next(undefined);
    this.filterCtrl.setValue('');
    this.manufacturerSelectedList$.next(this.manufacturerList);
    this.manufacturer.setValue([]);
  }

  removeManufacturer(manufacturerToRemove: string): void {
    const updated = (this.manufacturer.value ?? []).filter(m => m !== manufacturerToRemove);
    this.manufacturer.setValue(updated);
  }
}
